import {
	Component,
	ComponentInterface,
	Element,
	h,
	Host,
	Listen,
	State,
	Watch,
} from '@stencil/core';
import throttle from 'lodash.throttle';
import { Breakpoint, getCurrentBreakpoint } from '../_helpers/breakpoints';
import { removeInlineStylesFromHead } from '../_helpers/content-security-policy';
import {
	startObserving,
	stopObserving,
} from '../_helpers/moveInlineStylesToHeadObserver';

let observer;

@Component({
	tag: 'ds-featured-article',
})
export class FeaturedArticle implements ComponentInterface {
	@Element() el: HTMLDsFeaturedArticleElement;
	@State() currentSlideIndex = 1;
	@State() currentBreakpoint: Breakpoint = getCurrentBreakpoint();
	grabbing = false;
	// Initial X position of the current slide
	xPositionOfSlide: number;
	// Initial X position of mouse cursor while dragging
	startX: number;
	prevBtn: HTMLButtonElement;
	nextBtn: HTMLButtonElement;
	slideHeight: number;
	numberOfSlides: number;
	numberOfCards: number;
	totalNumberOfCardSlides: number;
	slides: NodeListOf<HTMLDivElement>;
	cards: NodeListOf<HTMLDivElement>;
	cardWidth: number;
	slidesContainer: HTMLDivElement;
	slidesContainerXPosition: number;
	outerContainer: HTMLDivElement;
	translationType: 'cards' | 'slides';
	numOfVisibleCardsMap = {
		[Breakpoint.XL]: 3,
		[Breakpoint.LG]: 3,
		[Breakpoint.MD]: 2,
		[Breakpoint.SM]: 2,
		[Breakpoint.XS]: 1,
	};

	componentWillLoad() {
		this.slides = this.el.querySelectorAll(`[data-slide]`);
		this.cards = this.el.querySelectorAll(`[data-card]`);
		this.numberOfSlides = this.slides.length;
		this.numberOfCards = this.cards.length;
		this.slidesContainer = this.el.querySelector('[data-slide-container]');

		this.nextBtn = this.el.querySelector('[data-next]');
		this.nextBtn?.setAttribute('role', 'none');
		this.prevBtn = this.el.querySelector('[data-prev]');
		this.prevBtn?.setAttribute('role', 'none');

		if (this.numberOfSlides > 0) {
			this.translationType = 'slides';
		} else {
			this.translationType = 'cards';
			this.outerContainer = this.el.querySelector('[data-outer-container]');
		}

		this.initCarousel();
		if (this.translationType === 'slides' && this.numberOfSlides >= 1) {
			this.setInitialSlidePositions();
		}
	}

	componentDidLoad(): void {
		this.nextBtn?.children[0].setAttribute('role', 'button');
		this.nextBtn?.children[0].setAttribute('aria-label', 'next');

		this.prevBtn?.children[0].setAttribute('role', 'button');
		this.prevBtn?.children[0].setAttribute('aria-label', 'previous');

		this.slidesContainerXPosition = 0;
		if (window.MutationObserver) {
			startObserving(this.el, observer);
		}
	}

	disconnectedCallback() {
		this.removeEventListeners();
		removeInlineStylesFromHead(this.el.id);
		if (window.MutationObserver) {
			stopObserving(observer);
		}
	}

	@Listen('resize', { target: 'window', passive: true })
	windowResizeHandler() {
		this.currentBreakpoint = getCurrentBreakpoint();
	}

	@Watch('currentBreakpoint')
	onBreakpointChange(currentBreakpoint: Breakpoint) {
		if (this.cards && this.cards.length) {
			this.totalNumberOfCardSlides = Math.ceil(
				this.cards.length / this.numOfVisibleCardsMap[currentBreakpoint]
			);

			// Shows or hides carousel navigation on breakpoint change, if there more cards then is
			// visible by default for a breakpoint it will show navigation
			if (this.numOfVisibleCardsMap[currentBreakpoint] >= this.cards.length) {
				this.hideCarouselNavigation();
			} else {
				this.showCarouselNavigation();
			}
		}

		if (
			currentBreakpoint === Breakpoint.SM ||
			currentBreakpoint === Breakpoint.XS
		) {
			this.renderSlideIndex(this.currentSlideIndex);
		}

		if (this.currentSlideIndex === 1) {
			this.setInitialSlidePositions();
		}

		if (this.translationType === 'cards') {
			this.resetTranslation();
		}

		if (this.translationType === 'slides' && this.numberOfSlides >= 1) {
			this.setInitialSlidePositions();
		}

		this.initCarousel();
	}

	// We are watching changes of currentSlideIndex because we need to disable
	// slider buttons if there are no more slides left or right of the current slide
	// and to update number of current slide.
	@Watch('currentSlideIndex')
	onSlideIndexUpdate(newState: number) {
		this.nextBtn.disabled = false;
		this.prevBtn.disabled = false;

		const total =
			this.translationType === 'slides'
				? this.numberOfSlides
				: this.totalNumberOfCardSlides;

		if (newState + 1 > total) {
			this.nextBtn.disabled = true;
		}
		if (newState - 1 < 1) {
			this.prevBtn.disabled = true;
		}

		this.renderSlideIndex(this.currentSlideIndex);
	}

	initCarousel() {
		if (this.translationType === 'slides') {
			if (this.numberOfSlides <= 1) {
				this.hideCarouselNavigation();
				this.adjustStylesForSingleSlide();
			} else {
				this.enableDragAbility();
				this.enableCarouselNavigation();
				this.renderSlideIndex(this.currentSlideIndex);
				this.renderTotalNumberOfSlides(this.numberOfSlides);
			}
		} else {
			if (this.cards) {
				const currentBreakpoint = getCurrentBreakpoint();
				this.totalNumberOfCardSlides = Math.ceil(
					this.cards.length / this.numOfVisibleCardsMap[currentBreakpoint]
				);
				if (this.cards.length > this.numOfVisibleCardsMap[currentBreakpoint]) {
					this.renderSlideIndex(this.currentSlideIndex);
					this.renderTotalNumberOfSlides(this.cards.length);
					this.enableCarouselNavigation();
					this.enableDragAbility();
					this.disableAccessibilityForHiddenCards();
				} else {
					this.hideCarouselNavigation();
				}

				if (this.cards.length <= 3) {
					const secondChild = this.el.children[1];
					if (secondChild && secondChild.children.length > 0) {
						const firstChild = secondChild.children[0];
						firstChild.setAttribute('three-or-less', 'true');
					}
				} else {
					if (this.el.children[1].children[0].hasAttribute('three-or-less')) {
						this.el.children[1].children[0].removeAttribute('three-or-less');
					}
				}
			}
		}
	}

	getSlide(index: number): HTMLDivElement {
		return this.el.querySelector(`[data-slide="${index}"]`);
	}

	getSlideLeftPadding() {
		const currentSlide = this.getSlide(this.currentSlideIndex);
		if (currentSlide) {
			return Number(
				window.getComputedStyle(currentSlide).paddingLeft.slice(0, -2)
			);
		}
		return 0;
	}

	// Sets height of slide container and translate slides to their initial positions
	setInitialSlidePositions() {
		if (this.slides && this.slides.length && this.slidesContainer) {
			// Padding on both sides of the slide
			const paddingWidth = this.getSlideLeftPadding() * 2;

			const slideHeights = [];

			//need to set current slide to 1 here to reset data controls
			this.currentSlideIndex = 1;

			// setting the position to 0 for first slide as it was causing an issue of staying outside of the container
			this.slides[0].style.transform = `translateX(0)`;
			const callToActionAnchor: HTMLAnchorElement =
				this.slides[0].querySelector('a[tabindex]');
			callToActionAnchor.tabIndex = 0;

			this.slides.forEach((slide, i) => {
				const { height } = slide.getBoundingClientRect();
				slideHeights.push(height);
				if (i > 0) {
					slide.style.transform = `translateX(calc(${
						i * 100
					}% + ${paddingWidth}px))`;
				}
			});

			const highestHeight = Math.max(...slideHeights);

			// Setting height of the slide container to be the highest height of all slides
			if (this.numberOfSlides > 1) {
				this.slidesContainer.style.minHeight = `${Math.ceil(highestHeight)}px`;
			}
		}
	}

	enableCarouselNavigation() {
		if (this.nextBtn && this.prevBtn) {
			this.prevBtn.disabled = true;
			this.prevBtn.addEventListener('click', this.onPrevSlide, {
				capture: true,
			});
			this.nextBtn.addEventListener('click', this.onNextSlide, {
				capture: true,
			});
		}
	}

	// Updates current slide in the html
	renderSlideIndex(index: number) {
		const previousCurrentSlideEl: HTMLSpanElement =
			this.el.querySelector('[current-slide]');
		if (previousCurrentSlideEl) {
			previousCurrentSlideEl.removeAttribute('current-slide');
		}
		const currentSlideNumberEl: HTMLSpanElement = this.el.querySelector(
			'[data-current-slide]'
		);
		const currentSlideEl: HTMLSpanElement = this.el.querySelector(
			`[data-slide="${index}"]`
		);
		if (currentSlideNumberEl) {
			currentSlideNumberEl.innerHTML = String(index);
			if (this.numberOfSlides > 0) {
				currentSlideEl.setAttribute('current-slide', '');
			}
		}
	}

	translateSlidesHorizontally(direction: 'forward' | 'backward') {
		const paddingWidth = this.getSlideLeftPadding() * 2;

		if (direction === 'forward') {
			this.currentSlideIndex += 1;
		} else {
			this.currentSlideIndex -= 1;
		}

		this.slides.forEach((slide, i) => {
			if (this.currentSlideIndex === i + 1) {
				const callToActionAnchor: HTMLAnchorElement =
					slide.querySelector('a[tabindex]');

				callToActionAnchor.tabIndex = 0;

				slide.style.transform = `translateX(${
					100 * (i - (this.currentSlideIndex - 1))
				})`;
			} else {
				const callToActionAnchor: HTMLAnchorElement =
					slide.querySelector('a[tabindex]');

				callToActionAnchor.tabIndex = -1;
				const positionXPercent = 100 * (i - (this.currentSlideIndex - 1));
				let cssCalc: string;
				if (i < this.currentSlideIndex) {
					cssCalc = `${positionXPercent}% - ${paddingWidth}px`;
				} else {
					cssCalc = `${positionXPercent}% + ${paddingWidth}px`;
				}
				slide.style.transform = `translateX(calc(${cssCalc}))`;
			}
		});
	}

	translateCardsHorizontally = throttle((direction: 'forward' | 'backward') => {
		// Has to be set before other logic
		if (direction === 'backward') {
			this.currentSlideIndex -= 1;
		}

		const { width } = this.slidesContainer
			.querySelector('[data-card]')
			.getBoundingClientRect();

		const breakpoint = getCurrentBreakpoint();

		const currentIndex =
			direction === 'backward'
				? this.currentSlideIndex - 1
				: this.currentSlideIndex;

		const translateDistance =
			currentIndex * width * this.numOfVisibleCardsMap[breakpoint] +
			this.numOfVisibleCardsMap[breakpoint] * currentIndex * 16;

		this.slidesContainer.style.transition = 'transform 500ms ease-in-out';
		if (this.currentSlideIndex !== this.totalNumberOfCardSlides) {
			this.slidesContainer.style.transform = `translateX(-${translateDistance}px)`;
		} else {
			this.slidesContainer.style.transform = `translateX(${this.slidesContainerXPosition}px)`;
		}

		// Has to be set at the end
		if (direction === 'forward') {
			this.currentSlideIndex += 1;
		}

		// Timeout is set because we are waiting for animation to finish
		setTimeout(() => {
			this.disableAccessibilityForHiddenCards();
		}, 550);
	}, 500);

	// Remove tabbing ability from a hidden cards
	disableAccessibilityForHiddenCards() {
		if (this.cards && this.cards.length) {
			Array.from(this.cards).forEach((card) => {
				const { left, right } = card.getBoundingClientRect();

				const link: HTMLAnchorElement = card.querySelector('a');

				if (link) {
					if (left < 0 || right > window.innerWidth) {
						link.tabIndex = -1;
					} else {
						link.tabIndex = 0;
					}
				}
			});
		}
	}

	// This method resets translateX of slides/cards container and returns slides/slides to initial position
	resetTranslation() {
		if (this.slidesContainer) {
			this.slidesContainer.style.transition = 'transform 500ms ease-in-out';
			this.slidesContainer.style.transform = `translateX(0px)`;
			this.currentSlideIndex = 1;
		}
	}

	onNextSlide = () => {
		if (
			this.translationType === 'slides' &&
			this.currentSlideIndex >= 1 &&
			this.currentSlideIndex < this.numberOfSlides
		) {
			this.translateSlidesHorizontally('forward');
		}

		if (
			this.translationType === 'cards' &&
			this.currentSlideIndex + 1 <= this.totalNumberOfCardSlides
		) {
			this.translateCardsHorizontally('forward');
		}
	};

	onPrevSlide = () => {
		if (
			this.translationType === 'slides' &&
			this.currentSlideIndex > 1 &&
			this.currentSlideIndex <= this.numberOfSlides
		) {
			this.translateSlidesHorizontally('backward');
		}

		if (this.translationType === 'cards' && this.currentSlideIndex > 1) {
			this.translateCardsHorizontally('backward');
		}
	};

	// Handle user slide dragging with throttling because of the high number of fired events
	dragSlidesTo = (direction: 'next' | 'prev') =>
		throttle(() => {
			if (direction === 'next') {
				this.onNextSlide();
			} else {
				this.onPrevSlide();
			}
			this.slidesContainer.style.transition = `transform 500ms ease-in-out`;
			this.slidesContainer.style.transform = `translateX(0px)`;
			this.grabbing = false;
		}, 1000);

	dragCardsTo = (direction: 'next' | 'prev') =>
		throttle(() => {
			if (
				direction === 'next' &&
				this.currentSlideIndex < this.totalNumberOfCardSlides
			) {
				this.grabbing = false;
				return this.translateCardsHorizontally('forward');
			}

			if (direction === 'prev' && this.currentSlideIndex > 1) {
				this.grabbing = false;
				return this.translateCardsHorizontally('backward');
			} else {
				this.slidesContainer.style.transition = `transform 500ms ease-in-out`;
				this.slidesContainer.style.transform = `translateX(${this.slidesContainerXPosition}px)`;
			}
		}, 1000);

	setCardPointerEvents(pointerEvent: 'none' | 'initial') {
		this.cards.forEach((card) => {
			card
				.querySelectorAll('a')
				?.forEach((link) => (link.style.pointerEvents = pointerEvent));
		});
	}

	onMouseMove = (e: MouseEvent) => {
		if (this.grabbing) {
			this.xPositionOfSlide = e.clientX;
			this.handleDragTo();
		}
	};

	onTouchMove = (e: TouchEvent) => {
		if (this.grabbing) {
			this.xPositionOfSlide = e.touches[0].clientX;
			this.handleDragTo();
		}
	};

	isSliderContainerInAnimation = (): boolean => {
		return this.slidesContainer.getAnimations().length > 0;
	};

	handleDragTo = () => {
		this.setCardPointerEvents('none');
		const diff = this.xPositionOfSlide - this.startX;
		let isDragEnoughNext = false;
		let isDragEnoughPrev = false;

		this.slidesContainer.style.transition = `none`;
		if (this.translationType === 'slides') {
			isDragEnoughNext = diff <= -250;
			isDragEnoughPrev = diff >= 250;
			if (isDragEnoughNext) {
				return this.dragSlidesTo('next')();
			} else if (isDragEnoughPrev) {
				return this.dragSlidesTo('prev')();
			}
			this.slidesContainer.style.transform = `translateX(${diff}px)`;
		} else {
			isDragEnoughNext = diff < -this.cardWidth / 2;
			isDragEnoughPrev = diff > this.cardWidth / 2;
			if (isDragEnoughNext) {
				return this.dragCardsTo('next')();
			} else if (isDragEnoughPrev) {
				return this.dragCardsTo('prev')();
			}
			this.slidesContainer.style.transform = `translateX(${
				this.slidesContainerXPosition + diff
			}px)`;
		}
	};

	onMouseDown = (e: MouseEvent) => {
		e.preventDefault();
		if (!this.isSliderContainerInAnimation()) {
			this.startX = e.clientX;
			this.setGrabbing();
		}
	};

	onTouchStart = (e: TouchEvent) => {
		if (!this.isSliderContainerInAnimation()) {
			this.startX = e.touches[0].clientX;
			this.setGrabbing();
		}
	};

	setGrabbing = () => {
		this.grabbing = true;
		this.slidesContainer.style.cursor = 'grabbing';
		if (this.translationType === 'cards') {
			const style = window.getComputedStyle(this.slidesContainer);
			const transform = style.getPropertyValue('transform');
			const translateX = parseInt(transform.split(',')[4]);
			this.slidesContainerXPosition = isNaN(translateX) ? 0 : translateX;
			const cardStyle = window.getComputedStyle(this.cards[0]);
			this.cardWidth = parseInt(cardStyle.getPropertyValue('width'));
		}
	};

	onMouseEnter = (e) => {
		e.stopPropagation();
		this.slidesContainer.classList.add('ds-cursor-grab');
	};

	onMouseUp = () => {
		this.grabbing = false;
		this.setCardPointerEvents('initial');
		this.slidesContainer.classList.add('ds-cursor-grab');
		const diff = this.xPositionOfSlide - this.startX;
		let isDragShort = false;

		if (this.translationType === 'slides') {
			isDragShort = !(diff <= -250) && !(diff >= 250);
		} else {
			isDragShort = diff < this.cardWidth / 2 && diff > -this.cardWidth / 2;
		}

		if (isDragShort) {
			this.slidesContainer.style.transition = `transform 500ms ease-in-out`;
			if (this.translationType === 'slides') {
				this.slidesContainer.style.transform = `translateX(0px)`;
			} else {
				this.slidesContainer.style.transform = `translateX(${this.slidesContainerXPosition}px)`;
			}
		}
	};

	enableDragAbility() {
		this.slidesContainer.addEventListener('mousedown', this.onMouseDown, {
			capture: true,
		});
		this.slidesContainer.addEventListener('mouseenter', this.onMouseEnter);
		this.slidesContainer.addEventListener('mouseup', this.onMouseUp, {
			capture: true,
		});
		this.slidesContainer.addEventListener('mousemove', this.onMouseMove, {
			passive: true,
			capture: true,
		});
		this.slidesContainer.addEventListener('mouseleave', this.onMouseUp);
		this.slidesContainer.addEventListener('touchstart', this.onTouchStart, {
			passive: true,
		});
		this.slidesContainer.addEventListener('touchend', this.onMouseUp);
		this.slidesContainer.addEventListener('touchcancel', this.onMouseUp);
		this.slidesContainer.addEventListener('touchmove', this.onTouchMove, {
			passive: true,
		});
	}

	hideCarouselNavigation() {
		const navigationEl: HTMLDivElement =
			this.el.querySelector('[data-controls]');

		if (navigationEl) {
			navigationEl.style.display = 'none';
			navigationEl.style.visibility = 'hidden';
		}
	}

	showCarouselNavigation() {
		const navigationEl: HTMLDivElement =
			this.el.querySelector('[data-controls]');

		if (navigationEl) {
			navigationEl.style.display = 'block';
			navigationEl.style.visibility = 'visible';
		}
	}

	adjustStylesForSingleSlide() {
		const currentSlide: HTMLDivElement = this.getSlide(this.currentSlideIndex);

		if (currentSlide) {
			currentSlide.classList.remove('ds-fa-slide');
		}
	}

	renderTotalNumberOfSlides(total: number) {
		const slidesTotalEl: HTMLSpanElement = this.el.querySelector(
			'[data-total-slides]'
		);
		if (slidesTotalEl && total) {
			slidesTotalEl.innerHTML = String(total);
		}
	}

	// Important if component is used in Angular or other frontend frameworks
	removeEventListeners() {
		if (this.slidesContainer) {
			this.slidesContainer.removeEventListener('mousedown', this.onMouseDown);
			this.slidesContainer.removeEventListener('mouseenter', this.onMouseEnter);
			this.slidesContainer.removeEventListener('mouseup', this.onMouseUp);
			this.slidesContainer.removeEventListener('mousemove', this.onMouseMove);
			this.slidesContainer.removeEventListener('mouseleave', this.onMouseUp);
			this.slidesContainer.removeEventListener('touchstart', this.onTouchStart);
			this.slidesContainer.removeEventListener('touchend', this.onMouseUp);
			this.slidesContainer.removeEventListener('touchcancel', this.onMouseUp);
			this.slidesContainer.removeEventListener('touchmove', this.onTouchMove);
		}

		if (this.prevBtn) {
			this.prevBtn.removeEventListener('click', this.onPrevSlide);
		}

		if (this.nextBtn) {
			this.nextBtn.removeEventListener('click', this.onNextSlide);
		}
	}

	render() {
		return (
			<Host>
				<slot></slot>
			</Host>
		);
	}
}
